import React, { useEffect } from 'react';

import { Accordion as MUIExpansionPanel, AccordionDetails as MUIExpansionPanelDetails, AccordionSummary as MUIExpansionPanelSummary, withStyles } from '@material-ui/core';

import Icon from '@cobliteam/ui/icons';

import { colors, fonts } from '../styles';

const StyledExpansionPanel = withStyles({
  root: {
    boxShadow: 'none',
    borderBottom: `1px solid ${colors.gray.light}`,
    padding: 0,

    '&:before': {
      opacity: 0,
    },

    '&$expanded': {
      margin: 0,
    },
  },
  expanded: {
    padding: '0 0 20px 0',
  },
  disabled: {
    backgroundColor: colors.white,
  },
})(MUIExpansionPanel);

export const ExpansionPanelDetails = withStyles({
  root: {
    padding: 0,
    fontSize: fonts.size.small,
    lineHeight: 1.36,
    display: 'block',
    color: colors.gray.dark,
  },
})(MUIExpansionPanelDetails);

export interface AccordionItemProps {
  summary: React.ReactNode;
  children: React.ReactNode;
  expansionPanelClassName?: string;
  summaryClassName?: string;
  disabled?: boolean;
  disableToggle?: boolean;
  keepMounted?: boolean;
  expandedByDefault?: boolean;
  forceExpansion?: boolean;
  forceClosed?: boolean;
  expansionPanelSummaryMinHeight?: string;
  onChange?: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export const AccordionItem: React.FC<AccordionItemProps> = ({
  summary,
  children,
  expansionPanelClassName,
  summaryClassName,
  disabled,
  disableToggle,
  keepMounted = true,
  expandedByDefault = false,
  forceExpansion,
  forceClosed,
  expansionPanelSummaryMinHeight,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(expandedByDefault || false);
  const ExpansionPanelSummary = withStyles({
    root: {
      minHeight: expansionPanelSummaryMinHeight !== undefined ? expansionPanelSummaryMinHeight : 88,
      backgroundColor: colors.white,
      padding: 0,
      '&:hover': {
        backgroundColor: colors.custom.lightGray50Alternative,
      },
      '&$disabled': {
        opacity: 1,
        cursor: 'default',

        '& label, & div + span': {
          opacity: 0.5,
        },
      },
      '&$expanded': {
        minHeight: expansionPanelSummaryMinHeight !== undefined ? expansionPanelSummaryMinHeight : 88,
      },
    },
    disabled: {}, // MUI needs this so I can override the disabled styling
    content: {
      padding: '20px 10px 20px 20px',
      margin: 0,
      display: 'flex',
      justifyContent: 'space-between',

      '&$expanded': {
        margin: 0,
      },
    },
    expanded: {
      margin: 0,
    },
    expandIcon: {
      padding: '12px 20px 12px 10px',
      margin: 0,
      color: colors.gray.dark,
      transition: 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

      '& svg': {
        fontSize: 30,
        color: colors.gray.default,
      },
    },
  })(MUIExpansionPanelSummary);

  const handleOnChange = (event: React.ChangeEvent<{}>, expanded: boolean) => {
    setIsOpen(expanded);

    if (onChange) {
      onChange(event, expanded);
    }
  };

  useEffect(() => {
    if (forceExpansion) {
      setIsOpen(forceExpansion);
    }
  }, [forceExpansion]);

  useEffect(() => {
    if (forceClosed) {
      setIsOpen(false);
    }
  }, [forceClosed]);

  return (
    <StyledExpansionPanel
      className={expansionPanelClassName || ''}
      disabled={disabled || disableToggle}
      onChange={handleOnChange}
      defaultExpanded={expandedByDefault}
      expanded={isOpen}
    >
      <ExpansionPanelSummary IconButtonProps={{ disableRipple: true }} className={summaryClassName || ''} expandIcon={disabled ? null : <Icon name='chevron-down' />}>
        {summary}
      </ExpansionPanelSummary>
      {(keepMounted || isOpen) && <ExpansionPanelDetails>{children}</ExpansionPanelDetails>}
    </StyledExpansionPanel>
  );
};
